import React, { useState, useEffect } from 'react';
import axios from 'axios';
import moment from 'moment';
import * as Yup from 'yup';
import {
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  FormControl,
  InputLabel,
  Select,
  Paper,
  MenuItem,
  Input,
  InputAdornment,
  IconButton,
  Backdrop,
  CircularProgress,
  Stack
} from '@material-ui/core';
import ReceiptIcon from '@material-ui/icons/Receipt';
import LocalHospitalIcon from '@material-ui/icons/LocalHospital';
import { useFormik, Form, FormikProvider } from 'formik';
import { baseUrl, controlAccounts } from '../../utils/constants';
import { useAccountingContext } from '../../context/accounting_context';
import { useConstantsContext } from '../../context/constants_context';
import Controls from '../controls/Controls';
import JournalAnalysis from './JournalAnalysis';
import JnrAnalysisLine from './JnrAnalysisLine';
import JournalSelfLineAnalysis from './JournalSelfLineAnalysis';
import JournalSelfAnalysis from './JournalSelfAnalysis';

export default function JournalSelf() {
  const dateToday = new Date();
  const { payCycles } = useConstantsContext();
  const { refIdSelf, setRefIdSelf } = useAccountingContext();
  const [loading, setLoading] = useState(false);
  const [openPopup, setOpenPopup] = useState(false);
  const [openPopupDiag, setOpenPopupDiag] = useState(false);
  const [notify, setNotify] = useState({ isOpen: false, message: '', type: 'success' });
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: '',
    subTitle: '',
    heading: '',
    color: '#000000'
  });
  const [currentJournal, setCurrentJournal] = useState({
    refId: 0,
    voucherNo: '',
    payCycleID: '',
    claimNo: '',
    details: '',
    chequeNo: '',
    userId: 0,
    controlAcc: '',
    debit: 0,
    credit: 0,
    dateProcessed: ''
  });

  useEffect(() => {
    getJournalById();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const JournalSchema = Yup.object().shape({
    voucherNo: Yup.string().required('you must specify the Voucher #'),
    controlAcc: Yup.string().required('you must specify the Control Account'),
    details: Yup.string().required('Journal details are required'),
    payCycleID: Yup.number()
      .required('you must specify the Transacting Month')
      .moreThan(0, 'you must specify the Transacting Month'),
    credit: Yup.number()
      .required('Credited Amount is required')
      .moreThan(0, 'Credited Amount has not been updated')
  });

  const formik = useFormik({
    initialValues: currentJournal,
    enableReinitialize: true,
    validationSchema: JournalSchema,
    onSubmit: (values) => {
      console.log('values found', values);
      values.dateProcessed = moment();
      if (values.journalId === '') {
        notifyPopup(`Voucher # not Selected, please select Voucher!`, 'error');
      } else {
        saveJournalEntry(values);
      }
    }
  });

  currentJournal.journalDate = moment(currentJournal.journalDate).format('yyyy-MM-DD');

  const { submitForm } = formik;

  const handleSubmit = () => {
    console.log('came here to submit');
    submitForm();
  };

  const getJournalById = async (event) => {
    if (refIdSelf === -1) {
      return;
    }
    setLoading(true);
    await axios(`${baseUrl}/api/accounting/getJournalSelfById?refId=${refIdSelf}`).then(
      (result) => {
        setLoading(false);
        console.log('returned journal info', result);
        setCurrentJournal(result.data.result);
      },
      (error) => {
        notifyPopup(`Error in Journal Info!: ${error}`, 'error');
      }
    );
  };

  const saveJournalEntry = async (client) => {
    setLoading(true);
    const jsonClient = [];
    jsonClient.push(client);
    console.log('journal stringified now', JSON.stringify(jsonClient[0]));
    fetch(`${baseUrl}/api/accounting/saveJournalSelf`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(jsonClient[0])
    })
      .then((res) => res.json())
      .then(
        (res) => {
          console.log('returned Journal', res);
          setLoading(false);
          setCurrentJournal(res);
          setRefIdSelf(res.refId);
          notifyPopup(`Journal Entry Saved Successfully!`, 'success');
        },
        (error) => {
          setLoading(false);
          notifyPopup(`Error in Saving Journal Entry!: ${error}`, 'error');
        }
      );
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const handleLoadingClose = () => {
    setLoading(false);
  };

  const closePopup = () => {
    setOpenPopup(false);
  };

  const closePopupDiag = () => {
    setOpenPopupDiag(false);
  };

  const notifyPopup = (notifyMessage, notifyType) => {
    setNotify({
      isOpen: true,
      message: `${notifyMessage}`,
      type: `${notifyType}`
    });
  };

  // const { errors, touched, values, getFieldProps } = formik;

  /* eslint-disable camelcase */
  return (
    <FormikProvider value={formik}>
      <Form autoComplete="off" noValidate>
        <Typography component="span" variant="h6" gutterBottom>
          Self Payment Journal
        </Typography>
        <Card
          sx={{
            m: '30px 100px'
          }}
        >
          <CardContent>
            <Backdrop
              sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
              open={loading}
              onClick={handleLoadingClose}
            >
              <CircularProgress color="inherit" />
            </Backdrop>
            <Grid container spacing={2}>
              <Grid item xs={8} sm={8}>
                <Paper>
                  <Grid margin={2} container spacing={2}>
                    <Grid item xs={3} sm={5}>
                      <Controls.Input name="voucherNo" label="Voucher #" />
                    </Grid>
                    <Grid item xs={3} sm={10}>
                      <Controls.Input name="details" label="Details" />
                    </Grid>
                    <Grid item xs={3} sm={5}>
                      <Controls.Input name="chequeNo" label="Cheque #" />
                    </Grid>
                    <Grid item xs={3} sm={5}>
                      <Controls.SelectJson
                        name="controlAcc"
                        label="Control Account"
                        options={controlAccounts}
                      />
                    </Grid>
                    <Grid item xs={3} sm={5}>
                      <Controls.InputNumber name="debit" label="Debit" disabled />
                    </Grid>
                    <Grid item xs={3} sm={5}>
                      <Controls.InputNumber name="credit" label="Credit" />
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={4} mt={2}>
                <Paper variant="outlined">
                  <Grid item xs={12} sm={12} margin={2}>
                    <Controls.InputNumber name="refId" label="Journal Id" disabled />
                  </Grid>
                  <Grid item xs={12} sm={12} margin={2}>
                    <Controls.DateTime name="journalDate" label="Date" />
                  </Grid>
                  <Grid item xs={12} sm={12} margin={2}>
                    <FormControl fullWidth>
                      <InputLabel id="payCycleLbl">Period</InputLabel>
                      <Select
                        id="payCycleID"
                        value={formik.values.payCycleID}
                        name="payCycleID"
                        fullWidth
                        size="small"
                        variant="outlined"
                        onChange={(e) => {
                          formik.setFieldValue('payCycleID', e.target.value);
                          formik.setFieldValue('userId', sessionStorage.getItem('oldRefId'));
                        }}
                        error={formik.touched.payCycleID && Boolean(formik.errors.payCycleID)}
                        helpertext={formik.touched.payCycleID && formik.errors.payCycleID}
                      >
                        <MenuItem key="" value="">
                          Not Selected
                        </MenuItem>
                        {payCycles.map((item) => (
                          <MenuItem key={item.payCycleId} value={item.payCycleId}>
                            {item.payDate}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Paper>
              </Grid>
            </Grid>
            <Grid item xs={12} sm={12} m={2}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" mb="3">
                <Button
                  type="button"
                  variant="outlined"
                  color="secondary"
                  startIcon={<ReceiptIcon />}
                  onClick={() => handleSubmit()}
                >
                  Save Journal
                </Button>
                <Button
                  type="button"
                  variant="outlined"
                  color="secondary"
                  startIcon={<ReceiptIcon />}
                  disabled={formik.values.refId < 1}
                  onClick={() => setOpenPopup(true)}
                >
                  Add Analysis
                </Button>
              </Stack>
            </Grid>
            <Controls.Notification notify={notify} setNotify={setNotify} />
            <Controls.ConfirmDialog
              confirmDialog={confirmDialog}
              setConfirmDialog={setConfirmDialog}
            />
            <Controls.Popup
              title="Add Journal Analysis"
              openPopup={openPopup}
              setOpenPopup={setOpenPopup}
            >
              <JournalSelfLineAnalysis closePopup={closePopup} refId={refIdSelf} />
            </Controls.Popup>
          </CardContent>
        </Card>
        <JournalSelfAnalysis />
      </Form>
    </FormikProvider>
  );
}
